import {
  Button,
  Column,
  Columns,
  FlexBox,
  IconButton,
  Text,
  Tooltip,
} from '@dixa/lib-shared-habla';
import { sizes } from '@dixa/lib-shared-tokens';
import * as Popover from '@radix-ui/react-popover';
import type { Table as TanstackTable } from '@tanstack/react-table';
import type { SearchConversationEnriched } from 'lib-search-types';
import { ampli } from 'lib-shared-tracking';

import { appActions } from 'app/app.actions';
import { useAppDispatch } from 'utils/redux/useAppDispatch';

import type { ConversationsTableFeatures } from '../../conversationsTable.columns';

import { ConversationsTablePreferencesColumns } from './components/columns/conversationsTablePreferencesColumns';
import { ConversationsTablePreferencesDensity } from './components/density/conversationsTablePreferencesDensity';
import styles from './conversationsTablePreferences.module.scss';

export type ConversationsTablePreferencesProps = {
  /** The table whose columns the user chooses from. */
  table: TanstackTable<ConversationsTableFeatures, SearchConversationEnriched>;
};

/** A component that lets the user choose the density and the columns of the conversations table. */
export const ConversationsTablePreferences = ({
  table,
}: ConversationsTablePreferencesProps): JSX.Element | null => {
  const dispatch = useAppDispatch();
  const layout = table.options.meta?.layout;

  if (!layout) {
    return null;
  }

  const handleRestoreDefaults = async () => {
    const { isConfirmed } = await dispatch(
      appActions.showModal({
        modal: {
          type: 'app:confirm',
          arg: {
            title: 'Restore defaults?',
            description: (
              <FlexBox flexDirection="column" gap="300">
                <Text.Paragraph>
                  This will undo any customizations you've made to how the table
                  looks:
                </Text.Paragraph>

                <FlexBox as="ul" flexDirection="column" gap="100">
                  <li>Row spacing goes back to the default size</li>
                  <li>Column order returns to the original arrangement</li>
                  <li>Column widths return to their original sizes</li>
                  <li>
                    Hidden columns become visible again (and any added ones are
                    hidden)
                  </li>
                  <li>Pinned columns are unpinned</li>
                </FlexBox>
              </FlexBox>
            ),
            confirm: { label: 'Restore' },
          },
        },
      }),
    );

    if (isConfirmed) {
      layout.restoreDefaults();
      ampli.conversationsTableDefaultsRestored();
    }
  };

  const handleOpenChange = (isOpen: boolean) => {
    if (isOpen) {
      ampli.conversationsTablePreferencesOpened();
    }
  };

  return (
    <div className={styles.root}>
      <Popover.Root onOpenChange={handleOpenChange}>
        <Tooltip content="Customize the table" side="left">
          <Popover.Trigger asChild>
            <IconButton
              icon="preferences"
              variant="tertiary"
              aria-label="Customize the table"
            />
          </Popover.Trigger>
        </Tooltip>

        <Popover.Portal>
          <Popover.Content
            className={styles.popover}
            side="bottom"
            align="end"
            sideOffset={parseInt(sizes.spacer.values[100])}
            collisionPadding={parseInt(sizes.spacer.values[100])}
          >
            <Columns>
              <Column size="300px" className={styles.column}>
                <Column.Row
                  className={styles.content}
                  scrollable
                  noHorizontalPadding
                  noVerticalPadding
                  noMinHeight
                >
                  <ConversationsTablePreferencesDensity layout={layout} />

                  <ConversationsTablePreferencesColumns
                    table={table}
                    layout={layout}
                  />
                </Column.Row>

                <Column.Row
                  borderTop
                  noMinHeight
                  noHorizontalPadding
                  noVerticalPadding
                  className={styles.footer}
                >
                  <Popover.Close asChild>
                    <Button variant="secondary" onClick={handleRestoreDefaults}>
                      Restore defaults
                    </Button>
                  </Popover.Close>
                </Column.Row>
              </Column>
            </Columns>
          </Popover.Content>
        </Popover.Portal>
      </Popover.Root>
    </div>
  );
};
